import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Youtube, Send, Menu, X, LayoutDashboard, LogOut, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useFontSize } from "@/context/FontSizeContext";
import { CONTACT, DISCLAIMER } from "@/lib/constants";
import api from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Revision Kits", to: "/revision-kits" },
  { label: "Mock Tests", to: "/mock-tests" },
  { label: "Courses", to: "/courses" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

function TopStrip() {
  return (
    <div className="flex h-1 w-full" aria-hidden="true">
      <div className="flex-1" style={{ background: "#FF9933" }} />
      <div className="flex-1" style={{ background: "#FFFFFF" }} />
      <div className="flex-1" style={{ background: "#138808" }} />
    </div>
  );
}

function UtilityBar() {
  const { dec, reset, inc } = useFontSize();
  return (
    <div style={{ background: "#0B2545" }} className="text-[#E0E8F5]">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-1.5 text-xs">
        <div className="flex items-center gap-4">
          <a href="#main-content" className="hover:underline" data-testid="skip-to-content">
            Skip to main content
          </a>
          <div className="hidden items-center gap-1 sm:flex" aria-label="Font size controls">
            <span className="mr-1 opacity-80">Font size:</span>
            <button onClick={dec} className="rounded border border-white/30 px-1.5 leading-tight hover:bg-white/10" data-testid="font-decrease" aria-label="Decrease font size">A-</button>
            <button onClick={reset} className="rounded border border-white/30 px-1.5 leading-tight hover:bg-white/10" data-testid="font-reset" aria-label="Reset font size">A</button>
            <button onClick={inc} className="rounded border border-white/30 px-1.5 leading-tight hover:bg-white/10" data-testid="font-increase" aria-label="Increase font size">A+</button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-1 hover:underline" data-testid="utility-email">
            <Mail size={13} /> <span className="hidden md:inline">{CONTACT.email}</span>
          </a>
          <a href={CONTACT.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline" data-testid="utility-youtube">
            <Youtube size={14} /> <span className="hidden md:inline">YouTube</span>
          </a>
          <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline" data-testid="utility-telegram">
            <Send size={13} /> <span className="hidden md:inline">Telegram</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function Ticker() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/ticker").then(({ data }) => setItems(data.map((d) => d.text))).catch(() => {});
  }, []);
  if (!items.length) return null;
  const doubled = [...items, ...items];
  return (
    <div style={{ background: "#FEF3C7", borderBottom: "1px solid #FDE68A" }} className="overflow-hidden">
      <div className="mx-auto flex max-w-7xl items-center">
        <span className="z-10 shrink-0 bg-[#1F3A68] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white" data-testid="ticker-label">
          What's New
        </span>
        <div className="ticker-wrap relative flex-1 overflow-hidden py-1.5" data-testid="ticker">
          <div className="ticker-track text-sm text-[#92400E]">
            {doubled.map((t, i) => (
              <span key={i} className="mx-6 inline-flex items-center font-medium">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  const authed = user && typeof user === "object";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <TopStrip />
      <UtilityBar />

      {/* Main header */}
      <header className="bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <Logo size={48} />
            <div>
              <div className="font-serif text-xl font-bold leading-tight text-[#0B2545] sm:text-2xl">
                UPSC With Arpita
              </div>
              <div className="text-[11px] text-gray-600 sm:text-xs">
                Your Trusted Companion for Civil Services Preparation
              </div>
            </div>
          </Link>
          <div className="hidden items-center gap-3 lg:flex">
            <span className="rounded-sm bg-[#16A34A]/10 px-2.5 py-1 text-xs font-semibold text-[#16A34A]">
              100% Free Revision Kits
            </span>
          </div>
        </div>
      </header>

      {/* Nav bar */}
      <nav style={{ background: "#1F3A68" }} className="sticky top-0 z-40 text-white shadow-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4">
          <ul className="hidden items-stretch md:flex">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-block px-4 py-3 text-sm font-medium transition-colors hover:bg-[#2C5282] ${isActive(n.to) ? "bg-[#0B2545]" : ""}`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>

          <button
            className="py-3 md:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>

          <div className="flex items-center gap-2 py-2">
            {authed ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="flex items-center gap-1.5 rounded-sm bg-[#FF9933] px-3 py-1.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-[#E68A2E]" data-testid="nav-dashboard-btn">
                  <LayoutDashboard size={15} /> {user.role === "admin" ? "Admin" : "My Dashboard"}
                </Link>
                <button onClick={handleLogout} className="hidden items-center gap-1 rounded-sm border border-white/40 px-3 py-1.5 text-sm hover:bg-white/10 sm:flex" data-testid="nav-logout-btn">
                  <LogOut size={14} /> Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="rounded-sm bg-[#FF9933] px-4 py-1.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-[#E68A2E]" data-testid="nav-login-btn">
                Login / Register
              </Link>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <ul className="border-t border-white/10 bg-[#1F3A68] md:hidden" data-testid="mobile-menu">
            {NAV.map((n) => (
              <li key={n.to} className="border-b border-white/10">
                <Link to={n.to} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label} <ChevronRight size={16} />
                </Link>
              </li>
            ))}
            {authed && (
              <li className="border-b border-white/10">
                <button onClick={handleLogout} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm" data-testid="mobile-logout">
                  <LogOut size={15} /> Logout
                </button>
              </li>
            )}
          </ul>
        )}
      </nav>

      <Ticker />

      <main id="main-content" className="flex-1">
        {children}
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer style={{ background: "#0B2545" }} className="mt-12 text-[#CBD5E1]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-serif text-lg font-bold text-white">UPSC With Arpita</span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[#93A4bd]">
            A free, subject-wise Revision Kit library and mentorship by educator Arpita Sharma for Civil Services aspirants.
          </p>
        </div>
        <div>
          <h4 className="font-serif text-sm font-bold uppercase tracking-wide text-white">Quick Links</h4>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/revision-kits" className="hover:text-white hover:underline" data-testid="footer-kits">Revision Kits</Link></li>
            <li><Link to="/courses" className="hover:text-white hover:underline" data-testid="footer-courses">Courses</Link></li>
            <li><Link to="/about" className="hover:text-white hover:underline" data-testid="footer-about">About Arpita</Link></li>
            <li><Link to="/privacy" className="hover:text-white hover:underline" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-white hover:underline" data-testid="footer-terms">Terms of Use</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-serif text-sm font-bold uppercase tracking-wide text-white">Connect With Us</h4>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href={CONTACT.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white hover:underline" data-testid="footer-youtube"><Youtube size={15} /> YouTube</a></li>
            <li><a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white hover:underline" data-testid="footer-telegram"><Send size={14} /> Telegram</a></li>
            <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-white hover:underline" data-testid="footer-email"><Mail size={14} /> Email</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-serif text-sm font-bold uppercase tracking-wide text-white">Contact</h4>
          <p className="mt-3 text-sm">
            <a href={`mailto:${CONTACT.email}`} className="hover:text-white hover:underline">{CONTACT.email}</a>
          </p>
          <p className="mt-2 text-xs text-[#93A4bd]">
            Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <p className="text-xs leading-relaxed text-[#93A4bd]" data-testid="footer-disclaimer">
            <strong className="text-[#CBD5E1]">Disclaimer:</strong> {DISCLAIMER}
          </p>
          <p className="mt-2 text-xs text-[#7488a8]">
            © {new Date().getFullYear()} UPSC With Arpita. For educational purposes only.
          </p>
        </div>
      </div>
    </footer>
  );
}
